import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import LogisticsPausedBanner from "@/components/LogisticsPausedBanner";
import { HERO_IMG, FEATURE_IMG_TYRE, FEATURE_IMG_MOBILITY, LOGO_URL } from "@/lib/constants";
import { BUSINESS, TRUST_BADGES } from "@/lib/business";
import { api, fileUrl } from "@/lib/api";
import { usePublicSettings } from "@/lib/settings";
import { StarRating } from "@/components/StarRating";
import { Zap, Wrench, Search, ArrowRight, Battery, Disc3, Cog, HelpCircle, ShieldCheck, Clock, Truck, Phone, MapPin, ShoppingBag, ImageOff, Quote } from "lucide-react";

const BADGE_ICONS = { "21-Day Warranty": ShieldCheck, "Free Quote": Zap, "Fast Turnaround": Clock, "Mobile Service": Truck };

const services = [
  { icon: Disc3, title: "Tyres & Wheels", text: "Punctures, replacements, alignment, bearings." },
  { icon: Wrench, title: "Brakes", text: "Disc / drum brake servicing, pads, cables." },
  { icon: Battery, title: "Electrical", text: "Battery checks, controllers, wiring, BMS." },
  { icon: Cog, title: "General Service", text: "Tighten, lubricate, full safety check-over." },
  { icon: HelpCircle, title: "Diagnostics", text: "Not sure what's wrong? Drop it in, we'll diagnose." },
];

const Landing = () => {
  const settings = usePublicSettings();
  const [featured, setFeatured] = useState([]);
  const [reviews, setReviews] = useState({ items: [], average_rating: 0, total_count: 0 });
  useEffect(() => {
    api.get("/products")
      .then((res) => setFeatured((res.data || []).slice(0, 6)))
      .catch(() => {});
    api.get("/reviews", { params: { limit: 6 } })
      .then((res) => setReviews(res.data))
      .catch(() => {});
  }, []);
  return (
    <div className="min-h-screen bg-spark-floor">
      <Navbar />
      <LogisticsPausedBanner />

      {/* HERO */}
      <section className="relative bg-spark-teal-deep text-white grain-overlay overflow-hidden" data-testid="landing-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20 lg:py-28 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6 relative z-10">
            <div className="inline-flex items-center gap-2 bg-spark-yellow text-spark-ink px-3 py-1.5 rounded-sm mb-6 font-display uppercase tracking-wider text-xs font-bold" data-testid="hero-eyebrow">
              <Zap className="size-3.5 fill-spark-ink" /> NZ Home Workshop · Drop-off Repairs
            </div>
            <h1 className="font-display uppercase tracking-tight text-5xl sm:text-6xl lg:text-7xl font-bold leading-[0.95]">
              Sparking life
              <br />
              back into your
              <br />
              <span className="text-spark-yellow">scooter.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-300 max-w-xl leading-relaxed">
              Honest, careful repairs for electric scooters and mobility scooters. Book a drop-off online, track every stage of the fix, pay when she's running again.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/book" className="btn-accent" data-testid="hero-book-btn">
                Book a Repair <ArrowRight className="size-4" />
              </Link>
              <Link to="/track" className="btn-outline-ink !border-white !text-white hover:!bg-white hover:!text-spark-ink" data-testid="hero-track-btn">
                <Search className="size-4" /> Track Your Job
              </Link>
            </div>
            <div className="mt-10 flex items-center gap-6 text-xs text-slate-400">
              <div className="flex items-center gap-2">
                <Zap className="size-4 text-spark-yellow fill-spark-yellow" />
                <span>Same-day diagnostics</span>
              </div>
              <div className="flex items-center gap-2">
                <Wrench className="size-4 text-spark-yellow" />
                <span>Genuine parts</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-6 relative">
            <div className="relative aspect-[4/3] sm:aspect-[5/4] overflow-hidden rounded-md border border-white/10">
              <img src={HERO_IMG} alt="Technician repairing scooter" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-tr from-spark-ink/60 via-transparent to-spark-teal/30" />
            </div>
            <div className="hidden sm:block absolute -bottom-6 -left-6 bg-spark-yellow text-spark-ink p-4 rounded-md max-w-[220px] rotate-[-2deg] shadow-md">
              <p className="font-display uppercase font-bold text-2xl leading-none">6 stage</p>
              <p className="text-xs mt-1 font-semibold">live tracking on every job</p>
            </div>
            <img
              src={LOGO_URL}
              alt=""
              aria-hidden
              className="hidden lg:block absolute -top-8 -right-8 size-32 opacity-30"
            />
          </div>
        </div>
      </section>

      {/* TRUST BADGES */}
      <section className="bg-white border-b border-spark-line" data-testid="landing-trust">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-2 lg:grid-cols-4 gap-6">
          {TRUST_BADGES.map((b) => {
            const Icon = BADGE_ICONS[b.title] || ShieldCheck;
            return (
              <div key={b.title} className="flex items-center gap-3">
                <div className="size-11 rounded-sm bg-spark-teal-deep text-spark-yellow flex items-center justify-center shrink-0">
                  <Icon className="size-5" />
                </div>
                <div>
                  <p className="font-display uppercase tracking-tight font-bold text-spark-ink leading-tight">{b.title}</p>
                  <p className="text-xs text-spark-slate">{b.subtitle}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* SERVICES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24" data-testid="landing-services">
        <div className="grid md:grid-cols-12 gap-8 mb-12">
          <div className="md:col-span-5">
            <p className="label-mono text-spark-teal mb-3">What we fix</p>
            <h2 className="font-display uppercase tracking-tight text-4xl sm:text-5xl font-bold leading-tight">
              Real repairs.
              <br />No nonsense.
            </h2>
          </div>
          <p className="md:col-span-7 md:pt-3 text-spark-slate text-base leading-relaxed">
            From a flat tyre on your daily commuter to a full electrical diagnosis on a 4-wheel mobility scooter — we cover the lot. If you're not sure what's wrong, that's fine, drop it in and we'll work it out.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-spark-line border border-spark-line rounded-md overflow-hidden">
          {services.map(({ icon: Icon, title, text }) => (
            <div key={title} className="bg-white p-6 flex gap-4 hover:bg-spark-teal-light transition-colors">
              <div className="size-12 rounded-sm bg-spark-teal-deep text-spark-yellow flex items-center justify-center shrink-0">
                <Icon className="size-6" />
              </div>
              <div>
                <h3 className="font-display uppercase tracking-tight text-xl font-bold mb-1">{title}</h3>
                <p className="text-sm text-spark-slate leading-relaxed">{text}</p>
              </div>
            </div>
          ))}
          <div className="bg-spark-teal-deep text-white p-6 flex flex-col justify-between">
            <div>
              <p className="label-mono text-spark-yellow mb-2">Ready when you are</p>
              <p className="font-display uppercase text-2xl font-bold leading-tight">Book a drop-off in under 90 seconds.</p>
            </div>
            <Link to="/book" className="btn-accent mt-6 w-fit" data-testid="services-book-btn">
              Book Now <ArrowRight className="size-4" />
            </Link>
          </div>
        </div>
        <div className="mt-6 text-center">
          <Link to="/services" className="label-mono text-spark-teal hover:text-spark-ink inline-flex items-center gap-1.5" data-testid="see-all-services">
            See full pricing & services <ArrowRight className="size-3.5" />
          </Link>
        </div>
      </section>

      {/* HOW IT WORKS / IMAGES */}
      <section className="bg-white border-y border-spark-line" data-testid="landing-how-it-works">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5 grid grid-cols-2 gap-3">
            <div className="aspect-square overflow-hidden rounded-md border border-spark-line">
              <img src={FEATURE_IMG_TYRE} alt="Tyre repair" className="w-full h-full object-cover" />
            </div>
            <div className="aspect-square overflow-hidden rounded-md border border-spark-line mt-8">
              <img src={FEATURE_IMG_MOBILITY} alt="Mobility scooter" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="lg:col-span-7">
            <p className="label-mono text-spark-teal mb-3">How it works</p>
            <h2 className="font-display uppercase tracking-tight text-4xl sm:text-5xl font-bold leading-tight mb-8">
              Four steps.
              <br />Back on the road.
            </h2>
            <ol className="space-y-5">
              {[
                ["01", "Book online", "Tell us what's up. Add photos if you can."],
                ["02", "Drop it off", "Bring her round at your booked time."],
                ["03", "We fix it", "Live status updates as work progresses."],
                ["04", "Pick it up", "Pay on collection, ride away."],
              ].map(([n, h, t]) => (
                <li key={n} className="flex gap-5 items-start border-b border-spark-line pb-5 last:border-0">
                  <span className="font-display uppercase text-spark-teal text-3xl font-bold w-12 shrink-0">{n}</span>
                  <div>
                    <p className="font-display uppercase tracking-tight text-xl font-bold">{h}</p>
                    <p className="text-spark-slate text-sm mt-1">{t}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* REVIEWS SHOWCASE */}
      {reviews.items.length > 0 && (
        <section className="bg-white border-y border-spark-line" data-testid="landing-reviews">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
            <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
              <div>
                <p className="label-mono text-spark-teal">Word from the workshop</p>
                <h2 className="font-display uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl font-bold leading-[0.95] mt-2 text-spark-ink">
                  Riders who've<br className="sm:hidden" /> trusted us
                </h2>
                <div className="flex items-center gap-3 mt-4">
                  <StarRating value={Math.round(reviews.average_rating)} size={20} />
                  <span className="font-display uppercase tracking-tight text-2xl font-bold text-spark-ink">{reviews.average_rating.toFixed(1)}</span>
                  <span className="text-sm text-spark-slate">· {reviews.total_count} review{reviews.total_count === 1 ? "" : "s"}</span>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Link to="/reviews" className="btn-outline-ink !py-2.5 !px-4 text-xs" data-testid="landing-reviews-all">All reviews</Link>
                <Link to="/reviews" className="btn-primary !py-2.5 !px-4 text-xs">Leave a review</Link>
              </div>
            </div>
            <ul className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {reviews.items.slice(0, 6).map((r) => (
                <li key={r.id} className="border border-spark-line rounded-md p-5 bg-spark-floor relative" data-testid={`landing-review-${r.id}`}>
                  <Quote className="size-5 text-spark-yellow absolute top-4 right-4" />
                  <StarRating value={r.rating} size={14} />
                  {r.title && <p className="font-display uppercase tracking-tight text-base font-bold text-spark-ink mt-3">{r.title}</p>}
                  <p className="text-sm text-spark-ink mt-2 line-clamp-5 leading-relaxed">{r.body}</p>
                  <p className="text-xs text-spark-slate mt-4 pt-3 border-t border-spark-line">
                    <span className="font-display uppercase tracking-wider font-bold text-spark-ink">{r.customer_name}</span>
                    {r.location && <> · {r.location}</>}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* FEATURED SHOP */}
      {featured.length > 0 && (
        <section className="bg-spark-teal-night text-white border-y border-white/5" data-testid="landing-featured-shop">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
            <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
              <div>
                <p className="text-[10px] uppercase tracking-[0.22em] text-spark-yellow font-bold">Workshop store</p>
                <h2 className="font-display uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl font-bold leading-[0.95] mt-2">
                  Parts, upgrades<br className="sm:hidden" /> &amp; merch
                </h2>
                <p className="text-slate-300 mt-3 max-w-xl text-sm sm:text-base">
                  Genuine spares straight from the bench. Free workshop pickup or NZ-wide tracked shipping.
                </p>
              </div>
              <Link to="/shop" className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-spark-yellow text-spark-ink font-display uppercase tracking-wider text-xs font-bold hover:bg-spark-yellow-dark" data-testid="landing-shop-cta">
                <ShoppingBag className="size-4" /> Shop all
              </Link>
            </div>
            <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
              {featured.map((p) => (
                <li key={p.id}>
                  <Link to={`/shop/p/${p.slug}`} className="group block rounded-xl overflow-hidden bg-white/[0.04] border border-white/[0.07] hover:border-spark-yellow transition-all duration-300" data-testid={`featured-${p.sku}`}>
                    <div className="aspect-square bg-spark-teal-deep/40 overflow-hidden relative">
                      {p.images?.[0] ? (
                        <img src={fileUrl(p.images[0].storage_path)} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-slate-600"><ImageOff className="size-8" /></div>
                      )}
                    </div>
                    <div className="p-3">
                      <p className="text-[9px] uppercase tracking-[0.18em] text-spark-yellow font-bold">{p.category}</p>
                      <p className="text-white text-sm font-semibold mt-1 line-clamp-2 leading-tight">{p.name}</p>
                      <p className="font-display uppercase tracking-tight text-lg font-bold text-spark-yellow mt-1.5">${Number(p.price).toFixed(0)}</p>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* CONTACT STRIP */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16" data-testid="landing-contact-strip">
        <div className="grid sm:grid-cols-3 gap-px bg-spark-line border border-spark-line rounded-md overflow-hidden">
          {settings.phone && (
            <a href={`tel:${(settings.phone || "").replace(/[^+\d]/g, "")}`} className="bg-white p-5 flex items-start gap-3 hover:bg-spark-yellow transition" data-testid="landing-phone">
              <Phone className="size-5 text-spark-teal shrink-0 mt-1" />
              <div>
                <p className="label-mono">Call us</p>
                <p className="font-display uppercase tracking-wider text-lg font-bold">{settings.phone}</p>
              </div>
            </a>
          )}
          {settings.email && (
            <a href={`mailto:${settings.email}`} className="bg-white p-5 flex items-start gap-3 hover:bg-spark-yellow transition break-all" data-testid="landing-email">
              <Wrench className="size-5 text-spark-teal shrink-0 mt-1" />
              <div>
                <p className="label-mono">Email</p>
                <p className="font-semibold">{settings.email}</p>
              </div>
            </a>
          )}
          {settings.address_line && (
            <div className="bg-white p-5 flex items-start gap-3" data-testid="landing-address">
              <MapPin className="size-5 text-spark-teal shrink-0 mt-1" />
              <div>
                <p className="label-mono">Drop-off</p>
                <p className="font-semibold">{settings.address_line}</p>
                {(settings.city || settings.region) && (
                  <p className="text-xs text-spark-slate">{[settings.city, settings.region].filter(Boolean).join(", ")}</p>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20" data-testid="landing-cta">
        <div className="bg-spark-teal rounded-md p-8 sm:p-12 text-white grid md:grid-cols-12 gap-6 items-center relative overflow-hidden">
          <Zap className="absolute -right-8 -bottom-8 size-64 text-spark-yellow/30 fill-spark-yellow/30 rotate-12" />
          <div className="md:col-span-8 relative z-10">
            <h2 className="font-display uppercase tracking-tight text-4xl sm:text-5xl font-bold">
              Got a sick scooter?
            </h2>
            <p className="mt-3 text-white/90 max-w-lg">
              Book a drop-off slot, send photos through the form, get a confirmation reference and live tracking link.
            </p>
          </div>
          <div className="md:col-span-4 flex flex-wrap gap-3 relative z-10">
            <Link to="/book" className="btn-accent" data-testid="cta-book-btn">Book Repair</Link>
            <Link to="/track" className="btn-outline-ink !border-white !text-white hover:!bg-white hover:!text-spark-teal" data-testid="cta-track-btn">
              Track Job
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Landing;
